

// /src/pages/CreateCategory.jsx
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";
import { FaArrowLeft } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useAddCategoryMutation } from "../toolkitServices/categoryApi";

// ---------- Shared UI tokens (consistent with other screens) ----------
const baseInput =
  "w-full px-3 py-2.5 border rounded-lg text-[14px] focus:outline-none";
const okRing =
  "border-gray-300 focus:ring-4 focus:ring-[#9481ce]/20 focus:border-[#9481ce]";
const errRing =
  "border-red-400 focus:ring-4 focus:ring-red-200 focus:border-red-500";

const CreateCategory = () => {
  const navigate = useNavigate();

  const [addCategory, { isLoading, error, isSuccess, data }] =
    useAddCategoryMutation();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty },
    setFocus,
  } = useForm({
    defaultValues: {
      name: "",
      shortname: "",
      slug: "",
      hsn: "",
      description: "",
    },
    mode: "onBlur",
  });

  // Focus on mount
  useEffect(() => {
    const id = requestAnimationFrame(() => setFocus("name"));
    return () => cancelAnimationFrame(id);
  }, [setFocus]);

  // Success: reset & refocus (no redirect)
  useEffect(() => {
    if (isSuccess && data?.success) {
      toast.success("Category added successfully!");
      reset();
      requestAnimationFrame(() => setFocus("name"));
    }
  }, [isSuccess, data, reset, setFocus]);

  // Error toast
  useEffect(() => {
    if (error) {
      const msg =
        error?.data?.msg ||
        error?.data?.message ||
        error?.error ||
        "Error adding category!";
      toast.error(msg);
    }
  }, [error]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === "Backspace") {
        e.preventDefault();
        navigate(-1);
      }
      if ((e.ctrlKey && e.key.toLowerCase() === "s") || (e.key === "Enter" && e.ctrlKey)) {
        e.preventDefault();
        handleSubmit(onSubmit)();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [navigate, handleSubmit]);

  const onSubmit = async (form) => {
    const payload = {
      name: (form.name || "").trim(),
      shortName: (form.shortname || "").trim().toUpperCase(),
      slug: (form.slug || "").trim(),
      hsn: (form.hsn || "").trim(),
      description: (form.description || "").trim(),
    };
    try {
      await addCategory(payload).unwrap();
    } catch {
      // toast handled in error effect
    }
  };

  const loading = isLoading || isSubmitting;

  return (
    <div className="px-4 py-4">
      {/* Compact Header (consistent with Create/Edit forms) */}
      <div className="max-w-5xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-2 py-2 flex items-center gap-3">
          <Link
            to={"/masters/category-master-list"}
            className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-gray-200 hover:bg-gray-100 transition"
            title="Back to Category List"
          >
            <FaArrowLeft className="text-[#6f5cc5] text-sm" />
          </Link>
          <div className="flex-1">
            <h1 className="text-xl md:text-2xl font-semibold text-[#4b3d91] leading-tight">
              Create Category
            </h1>
            <p className="text-xs text-gray-500">
              Tips: <kbd className="px-1 bg-gray-100 rounded border">Ctrl</kbd>+
              <kbd className="px-1 bg-gray-100 rounded border">S</kbd> or{" "}
              <kbd className="px-1 bg-gray-100 rounded border">Ctrl</kbd>+
              <kbd className="px-1 bg-gray-100 rounded border">Enter</kbd> to save,{" "}
              <kbd className="px-1 bg-gray-100 rounded border ml-2">Ctrl</kbd>+
              <kbd className="px-1 bg-gray-100 rounded border">Back Spc</kbd> go to list
            </p>
          </div>
        </div>
      </div>

      {/* Card — compact, no inner scroll; BORDER-ONLY validation */}
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.28 }}
        className="max-w-5xl mx-auto bg-white shadow-md rounded-xl mt-4 border border-gray-200 relative"
        onAnimationComplete={() => requestAnimationFrame(() => setFocus("name"))}
      >
        {/* Loading overlay */}
        {loading && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] flex items-center justify-center z-20 rounded-xl">
            <div className="animate-spin h-7 w-7 border-2 border-[#9481ce] border-t-transparent rounded-full" />
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 md:grid-cols-2 gap-4 p-5">
          {/* Name */}
          <div>
            <label className="block text-[13px] font-medium text-gray-700 mb-1">
              Name<span className="text-red-500">*</span>
            </label>
            <input
              {...register("name", { required: true, minLength: 2, maxLength: 120 })}
              aria-invalid={!!errors.name}
              placeholder="Enter category name"
              autoComplete="off"
              className={`${baseInput} ${!!errors.name ? errRing : okRing}`}
            />
          </div>

          {/* Short Name */}
          <div>
            <label className="block text-[13px] font-medium text-gray-700 mb-1">Short Name</label>
            <input
              {...register("shortname")}
              placeholder="Short name (e.g. GOLD, SILV)"
              autoComplete="off"
              className={`${baseInput} ${okRing}`}
            />
          </div>

          {/* Slug */}
          <div>
            <label className="block text-[13px] font-medium text-gray-700 mb-1">Slug</label>
            <input
              {...register("slug")}
              placeholder="slug-like-this (optional)"
              autoComplete="off"
              className={`${baseInput} ${okRing}`}
            />
          </div>

          {/* HSN Code */}
          <div>
            <label className="block text-[13px] font-medium text-gray-700 mb-1">HSN Code</label>
            <input
              {...register("hsn")}
              placeholder="Enter HSN code"
              autoComplete="off"
              className={`${baseInput} ${okRing}`}
            />
          </div>

          {/* Description (full width) */}
          <div className="md:col-span-2">
            <label className="block text-[13px] font-medium text-gray-700 mb-1">Description</label>
            <textarea
              {...register("description")}
              placeholder="Enter description"
              rows={2}
              className={`${baseInput} ${okRing} resize-y`}
            />
          </div>

          {/* Submit */}
          <div className="md:col-span-2 flex flex-col sm:flex-row gap-2 pt-1">
            <motion.button
              whileHover={{ scale: loading ? 1 : 1.01 }}
              whileTap={{ scale: loading ? 1 : 0.98 }}
              type="submit"
              className="inline-flex justify-center items-center w-full sm:w-auto px-4 py-2.5 rounded-lg font-medium shadow-sm bg-[#9481ce] hover:bg-[#7d6ec0] text-white disabled:opacity-60"
              disabled={isLoading}
            >
              {isLoading ? "Saving..." : "Create Category"}
            </motion.button>

            <Link
              to="/masters/category-master-list"
              className="inline-flex justify-center items-center w-full sm:w-auto px-4 py-2.5 rounded-lg font-medium border border-gray-300 bg-white hover:bg-gray-50 text-gray-700"
              title="Cancel and go back"
            >
              Cancel
            </Link>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default CreateCategory;
